import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { AbilityService } from 'src/app/core/services/ability/ability.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { StorageBrowser } from 'src/app/core/services/storage.service';
import { AddCustomTextDialogComponent } from '../components/add-default-texts-dialog/add-default-texts-dialog.component';
import { EditorSaveService } from '../services/custom-text-selection.service';
import { CustomTextService } from '../services/custom-text.service';
interface SelectedText {
  id: string;
  name: string;
  label: string;
  value: string;
}

@Component({
  selector: 'app-custom-text-select',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    FormsModule,
    MatInputModule,
    MatIconModule,
    MatAutocompleteModule,
    MatProgressSpinner,
  ],
  templateUrl: './custom-text-select.component.html',
  styleUrls: ['./custom-text-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppCustomTextSelectComponent),
      multi: true,
    },
  ],
})
export class AppCustomTextSelectComponent implements OnInit {
  searchText: string = '';
  filteredTexts: SelectedText[] = [];
  @ViewChild(MatAutocompleteTrigger) autoTrigger!: MatAutocompleteTrigger;
  @Output() textSelected = new EventEmitter<string>();
  @Input() label: string | null = null;
  @Input() maxLength: number | null = null;
  @Input() group: string | null = null;
  @Input() required?: boolean = false;
  @Input() disabled?: boolean = false;
  @Input() initialValue?: string = '';
  isLoading: boolean = false;
  private destroyRef = inject(DestroyRef);
  constructor(
    private editorSave: EditorSaveService,
    private ability: AbilityService,
    private customTextService: CustomTextService,
    private notificationService: NotificationService,
    private storageBrowser: StorageBrowser,

    private dialog: MatDialog
  ) {}
  customTexts: SelectedText[] = [];

  openAddSelectedTextDialog() {
    this.dialog
      .open(AddCustomTextDialogComponent, {
        width: '400px',
        data: {
          name: this.group,
          label: this.label,
          maxLength: this.maxLength,
          initialValue: this.initialValue,
        },
      })
      .afterClosed()
      .subscribe((res) => {
        this.customTexts.push({
          id: res.id,
          name: res.name,
          label: res.label,
          value: res.value,
        });
        this.filteredTexts = [...this.customTexts];
        this.textSelected.emit(res.value);
        setTimeout(() => {
          this.searchText = '';
          this.autoTrigger.closePanel();
        });
      });
  }

  ngOnInit() {
    this.loadTestTexts();
    this.editorSave.save$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ field, value }) => {
        if (field === this.group) {
          this.saveField(value);
        }
      });
  }

  saveField(value) {
    const payload = {
      departmentId: this.ability.departmentId,
      value: value,
      label: '',
      name: this.group,
    };
    payload.value = payload.value?.trim();
    this.isLoading = true;
    this.customTextService.updateCustomTexts(payload).subscribe({
      next: (res) => {
        this.customTexts.push({
          id: res.id,
          name: res.name,

          label: res.label,
          value: res.value,
        });
         this.isLoading = false;
           this.notificationService.success(
            `Saved Entry to ${this.label}`
          );
      },
      error: (err) => {
        if (err.error.message == 'REPORT_CONFIG.ERRORS.DUPLICATE') {
          this.notificationService.error(
            `Duplicate Entry Already exists ${this.label}`
          );
        } else {
          this.notificationService.error(`Error creating ${this.label}`);
        }

        this.isLoading = false;
      },
    });
  }

  loadTestTexts() {
    this.isLoading = true;
    this.customTextService.getCustomTextsList({ name: this.group }).subscribe({
      next: (data) => {
        this.customTexts = data.items.map((m) => ({
          id: m.id,
          name: m.name,
          label: m.label,
          value: m.value,
        }));

        this.filteredTexts = [...this.customTexts];
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading customTexts', error),
          (this.isLoading = false);
      },
      complete: () => {
        console.log('TestTexts loaded successfully');
        this.isLoading = false;
      },
    });
  }

  filterTexts(event: any) {
    const search =
      typeof event === 'string'
        ? event.toLowerCase()
        : event?.value?.toString().toLowerCase() || '';
    this.filteredTexts = this.customTexts.filter((d) =>
      d.value.toString().toLowerCase().includes(search)
    );
  }

  onSelectSelectedText(text: SelectedText) {
    if (!text) return;

    this.filteredTexts = [...this.customTexts];

    this.textSelected.emit(text.value);
    setTimeout(() => {
      this.searchText = '';
    });
  }

  deleteText(item: SelectedText, event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();

    // UI update

    this.notificationService.openDialog({
      title: 'Are you sure?',
      message: 'This action will delete this entry',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      onConfirm: () => {
        this.customTexts = this.customTexts.filter((t) => t.id !== item.id);
        this.filteredTexts = [...this.customTexts];
        this.isLoading = true;
        this.customTextService.deleteCustomText(item.id).subscribe({
          next: () => {
            console.log('Deleted');
            this.isLoading = false;
          },
          error: (err) => {
            console.error('Delete failed', err);
            this.isLoading = false;
            this.notificationService.error('Cannot Delete Entry');
          },
        });
      },
      onCancel: () => {},
    });
  }

  displayText(t: SelectedText): string {
    return t?.value || '';
  }
}
